import { Button, Input } from "@mantine/core";
import {
  IconBriefcase,
  IconBuilding,
  IconCurrencyDollar,
  IconFileText,
  IconId,
  IconMail,
  IconMapPin,
  IconPhone,
  IconPhoneCall,
  IconPrinter,
  IconSettings,
  IconTicket,
  IconUser,
  IconUsers,
  IconWorld,
} from "@tabler/icons-react";
import { Upload, notification } from "antd";
import { useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface CompanyData {
  id?: string;
  fax?: string;
  localPhoneNumber?: string;
  email?: string;
  capitalAmount?: string;
  business?: string;
  companyRepresentative?: string;
  name?: string;
  businessIdNumber?: string;
  invitationCode?: string;
  invoice?: string;
  companyAbbreviation?: string;
  website?: string;
  postalCode?: number;
  district?: string;
  countyAndCity?: string;
  contactDepartment?: string;
  contactTelephone?: string;
  logo?: string;
  contact?: string;
  address?: {
    city?: string;
    district?: string;
    postalCode?: number;
    address?: string;
  };
}

function EditCompanyListPage() {
  const { t } = useTranslation();
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [editCompanyData, setEditCompanyData] = useState<Partial<CompanyData>>(
    {}
  );
  const [uploadState, setUploadState] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>("");

  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();

  const {
    fax = "",
    localPhoneNumber = "",
    email = "",
    capitalAmount = "",
    business = "",
    companyRepresentative = "",
    name = "",
    businessIdNumber = "",
    invitationCode = "",
    invoice = "",
    companyAbbreviation = "",
    website = "",
    contact = "",
    contactDepartment = "",
    contactTelephone = "",
    address = { city: "", district: "", postalCode: undefined, address: "" },
  } = companyData || {};

  const {
    city = "",
    district: addressDistrict = "",
    postalCode: addressPostalCode,
    address: fullAddressValue = "",
  } = address || {};

  const handleSubmitEditCompany = async (editedData: Partial<CompanyData>) => {
    try {
      const { id, organizationId, organization, ...updateData } =
        editedData as any;
      if (updateData.capitalAmount !== undefined && updateData.capitalAmount !== "") {
        updateData.capitalAmount = Number(updateData.capitalAmount);
      } else {
        delete updateData.capitalAmount;
      }
      const result = await orpcClient.company.update(updateData);

      if (result) {
        api.success({
          message: `Notification`,
          description: <div>{t("companyInfo.saveSuccess")}</div>,
          placement: "topLeft",
          duration: 3,
        });
        navigate("/hrMainPage/companyInfo");
      } else {
        api.error({
          message: `Notification`,
          description: <div>{t("companyInfo.errorUpdatingData")}</div>,
          placement: "topLeft",
          duration: 0,
        });
        console.error("Error updating company data:", result);
      }
    } catch (error: any) {
      const backendMessage =
        error?.message === "Forbidden"
          ? t("companyInfo.forbidden")
          : error?.message;
      const issues: Array<{ path?: (string | number)[]; message?: string }> =
        error?.data?.issues ?? [];
      api.error({
        message: `Notification`,
        description: (
          <div>
            <div>{backendMessage || t("companyInfo.errorUpdatingData")}</div>
            {issues.length > 0 && (
              <ul style={{ marginTop: 8, paddingLeft: 18, listStyle: "disc" }}>
                {issues.map((issue, idx) => {
                  const fieldPath = (issue.path ?? []).join(".");
                  return (
                    <li key={idx}>
                      {fieldPath ? <strong>{fieldPath}: </strong> : null}
                      {issue.message}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ),
        placement: "topLeft",
        duration: 0,
      });
      console.error("Error updating company data:", error);
    }
  };

  const handleDataChange = (name: string, value: string) => {
    setCompanyData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setEditCompanyData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    const processedValue =
      field === "postalCode" ? Number(value) || 0 : value;
    setCompanyData((prevData) => {
      if (!prevData) return prevData;
      const updatedAddress = {
        ...prevData.address,
        [field]: processedValue,
      };
      return {
        ...prevData,
        address: updatedAddress,
      };
    });
    setEditCompanyData((prevData) => {
      const currentAddress = companyData?.address || {};
      const updatedAddress = {
        city: currentAddress.city || "",
        district: currentAddress.district || "",
        postalCode: currentAddress.postalCode || 0,
        address: currentAddress.address || "",
        [field]: processedValue,
      };
      return {
        ...prevData,
        address: updatedAddress,
      };
    });
  };

  const handleChangeImg = async (info: any) => {
    if (uploadState) return;
    setUploadState(true);

    const file = info.file.originFileObj as File;

    try {
      const { blobUrl, blobSasUrl } = await orpcClient.storage.createBlobSasUrl({
        fileName: file.name,
        contentType: file.type,
      });

      const uploadResponse = await fetch(blobSasUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      setLogoPreview(URL.createObjectURL(file));
      handleDataChange("logo", blobUrl);
      api.success({
        message: `Notification`,
        description: <div>{t("companyInfo.logoUploadSuccess")}</div>,
        placement: "topLeft",
        duration: 3,
      });
    } catch (error) {
      api.error({
        message: `Notification`,
        description: <div>{t("companyInfo.logoUploadError")}</div>,
        placement: "topLeft",
        duration: 0,
      });
      console.error("Error uploading logo:", error);
    }

    setUploadState(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await orpcClient.company.read({});
        if (result) {
          const processedData = {
            ...result,
            fax: result.fax ?? undefined,
            localPhoneNumber: result.localPhoneNumber ?? undefined,
            email: result.email ?? undefined,
            capitalAmount: result.capitalAmount?.toString() ?? undefined,
            business: result.business ?? undefined,
            companyRepresentative: result.companyRepresentative ?? undefined,
            businessIdNumber: result.businessIdNumber ?? undefined,
            invitationCode: result.invitationCode ?? undefined,
            invoice: result.invoice ?? undefined,
            companyAbbreviation: result.companyAbbreviation ?? undefined,
            website: result.website ?? undefined,
            contact: result.contact ?? undefined,
            logo: result.logo ?? undefined,
            contactDepartment: result.contactDepartment ?? undefined,
            contactTelephone: result.contactTelephone ?? undefined,
            address: result.address ?? undefined,
          };
          setCompanyData(processedData);
          if (result?.logo) {
            setLogoPreview(result.logo);
          }
        } else {
          api.error({
            message: `Notification`,
            description: <div>{t("companyInfo.errorFetchingData")}</div>,
            placement: "topLeft",
            duration: 0,
          });
        }
      } catch (error) {
        api.error({
          message: `Notification`,
          description: <div>{t("companyInfo.errorFetchingData")}</div>,
          placement: "topLeft",
          duration: 0,
        });
        console.error("Error fetching company data:", error);
      } finally {
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen p-8">
      {contextHolder}
      <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 space-y-5">
        <div>
          <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
            {name || t("companyInfo.title")}
          </div>
          <div className="mt-2 text-sm text-gray-500">
            {t("companyInfo.description")}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.basicInfo")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconBuilding size={13} />
                {t("companyInfo.companyName")}
              </label>
              <Input
                value={name}
                onChange={({ target: { value } }) =>
                  handleDataChange("name", value)
                }
                leftSection={<IconBuilding size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconId size={13} />
                {t("companyInfo.businessIdNumber")}
              </label>
              <Input
                value={businessIdNumber}
                onChange={({ target: { value } }) =>
                  handleDataChange("businessIdNumber", value)
                }
                leftSection={<IconId size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconUser size={13} />
                {t("companyInfo.representative")}
              </label>
              <Input
                value={companyRepresentative}
                onChange={({ target: { value } }) =>
                  handleDataChange("companyRepresentative", value)
                }
                leftSection={<IconUser size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconBriefcase size={13} />
                {t("companyInfo.business")}
              </label>
              <Input
                value={business}
                onChange={({ target: { value } }) =>
                  handleDataChange("business", value)
                }
                leftSection={<IconBriefcase size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconCurrencyDollar size={13} />
                {t("companyInfo.capital")}
              </label>
              <Input
                value={capitalAmount}
                onChange={({ target: { value } }) =>
                  handleDataChange("capitalAmount", value)
                }
                leftSection={<IconCurrencyDollar size={16} />}
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPhoneCall size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.contactInfo")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconMail size={13} />
                {t("companyInfo.email")}＊
              </label>
              <Input
                value={email}
                onChange={({ target: { value } }) =>
                  handleDataChange("email", value)
                }
                leftSection={<IconMail size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconPhone size={13} />
                {t("companyInfo.localPhone")}
              </label>
              <Input
                value={localPhoneNumber}
                onChange={({ target: { value } }) =>
                  handleDataChange("localPhoneNumber", value)
                }
                leftSection={<IconPhone size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconPrinter size={13} />
                {t("companyInfo.fax")}
              </label>
              <Input
                value={fax}
                onChange={({ target: { value } }) =>
                  handleDataChange("fax", value)
                }
                leftSection={<IconPrinter size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconUsers size={13} />
                {t("companyInfo.contact")}
              </label>
              <Input
                value={contact}
                onChange={({ target: { value } }) =>
                  handleDataChange("contact", value)
                }
                leftSection={<IconUsers size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconPhone size={13} />
                {t("companyInfo.phone")}
              </label>
              <Input
                value={contactTelephone}
                onChange={({ target: { value } }) =>
                  handleDataChange("contactTelephone", value)
                }
                leftSection={<IconPhone size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconBriefcase size={13} />
                {t("companyInfo.department")}
              </label>
              <Input
                value={contactDepartment}
                onChange={({ target: { value } }) =>
                  handleDataChange("contactDepartment", value)
                }
                leftSection={<IconBriefcase size={16} />}
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconMapPin size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.address")}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconBuilding size={13} />
                {t("companyInfo.city")}
              </label>
              <Input
                value={city}
                onChange={({ target: { value } }) =>
                  handleAddressChange("city", value)
                }
                leftSection={<IconBuilding size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconBuilding size={13} />
                {t("companyInfo.district")}
              </label>
              <Input
                value={addressDistrict}
                onChange={({ target: { value } }) =>
                  handleAddressChange("district", value)
                }
                leftSection={<IconBuilding size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconId size={13} />
                {t("companyInfo.postalCode")}
              </label>
              <Input
                value={addressPostalCode?.toString() ?? ""}
                onChange={({ target: { value } }) =>
                  handleAddressChange("postalCode", value)
                }
                leftSection={<IconId size={16} />}
              />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconMapPin size={13} />
                {t("companyInfo.fullAddress")}
              </label>
              <Input
                value={fullAddressValue}
                onChange={({ target: { value } }) =>
                  handleAddressChange("address", value)
                }
                leftSection={<IconMapPin size={16} />}
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconSettings size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.advancedSettings")}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconBuilding size={13} />
                {t("companyInfo.logo")}
              </label>
              <div className="flex items-center gap-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                <Upload
                  name="logo"
                  showUploadList={false}
                  onChange={handleChangeImg}
                  accept="image/*"
                >
                  <Button variant="default" disabled={uploadState}>
                    {uploadState
                      ? t("companyInfo.uploading")
                      : t("companyInfo.uploadLogo")}
                  </Button>
                </Upload>
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Company Logo"
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconWorld size={13} />
                  {t("companyInfo.website")}
                </label>
                <Input
                  value={website}
                  onChange={({ target: { value } }) =>
                    handleDataChange("website", value)
                  }
                  leftSection={<IconWorld size={16} />}
                />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconBuilding size={13} />
                  {t("companyInfo.companyAbbreviation")}
                </label>
                <Input
                  value={companyAbbreviation}
                  onChange={({ target: { value } }) =>
                    handleDataChange("companyAbbreviation", value)
                  }
                  leftSection={<IconBuilding size={16} />}
                />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-1.5 text-xs text-gray-500">
                  <IconCurrencyDollar size={13} />
                  {t("companyInfo.invoiceTitle")}
                </label>
                <Input
                  value={invoice}
                  onChange={({ target: { value } }) =>
                    handleDataChange("invoice", value)
                  }
                  leftSection={<IconCurrencyDollar size={16} />}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconTicket size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("companyInfo.invitationCode")}
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <IconId size={13} />
                {t("companyInfo.enterInvitationCode")}
              </label>
              <Input
                value={invitationCode}
                onChange={({ target: { value } }) =>
                  handleDataChange("invitationCode", value)
                }
                leftSection={<IconId size={16} />}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            variant="default"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => navigate(`/hrMainPage/companyInfo`)}
          >
            {t("header.backToCompanyList")}
          </Button>
          <Button
            variant="filled"
            color="gray"
            style={{ flex: 1 }}
            radius="md"
            onClick={() => handleSubmitEditCompany(editCompanyData)}
          >
            {t("companyInfo.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditCompanyListPage;
